import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ImpersonationService } from 'app/core/impersonation/impersonation.service';
import { ImpersonationSearchUser } from 'app/core/impersonation/impersonation.types';
import { catchError, debounceTime, distinctUntilChanged, Observable, of, switchMap, tap } from 'rxjs';

/**
 * @description
 * Diálogo para que un super_admin busque y seleccione un usuario a impersonar.
 * La búsqueda es con debounce (>= 2 chars). Los usuarios `super_admin` se muestran
 * deshabilitados (no impersonables). Al confirmar, `startImpersonation` recarga la app.
 */
@Component({
    selector: 'impersonation-search-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, TranslocoPipe],
    template: `
        <h2 mat-dialog-title class="flex items-center gap-3">
            <mat-icon class="text-amber-600" fontSet="fa" fontIcon="fa-user-secret"></mat-icon>
            {{ 'IMPERSONATION.TITLE' | transloco }}
        </h2>

        <mat-dialog-content class="flex flex-col">
            <mat-form-field class="w-full" subscriptSizing="dynamic">
                <mat-icon matPrefix fontSet="fa" fontIcon="fa-search" class="icon-size-4 mr-2"></mat-icon>
                <input
                    matInput
                    [formControl]="search"
                    [placeholder]="'IMPERSONATION.SEARCH_PLACEHOLDER' | transloco"
                    [attr.aria-label]="'IMPERSONATION.SEARCH_PLACEHOLDER' | transloco"
                    autocomplete="off" />
                @if (loading) {
                    <mat-progress-spinner matSuffix diameter="18" mode="indeterminate"></mat-progress-spinner>
                }
            </mat-form-field>

            @if (errorMsg) {
                <div class="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" aria-live="assertive">
                    {{ errorMsg }}
                </div>
            }

            <div class="mt-4 max-h-96 overflow-y-auto" role="list">
                @if (!loading && searched && !errorMsg && results.length === 0) {
                    <div class="py-8 text-center text-sm text-gray-500">{{ 'IMPERSONATION.NO_RESULTS' | transloco }}</div>
                }
                @for (u of results; track u.uuid) {
                    <div class="flex items-center gap-3 border-b border-gray-100 py-2" role="listitem">
                        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold uppercase text-gray-600">
                            {{ initials(u) }}
                        </div>
                        <div class="min-w-0 flex-1">
                            <div class="truncate text-sm font-medium">{{ u.firstname }} {{ u.lastname }}</div>
                            <div class="truncate text-xs text-gray-500">{{ u.email }}</div>
                            <div class="text-xs text-gray-400">
                                <span class="rounded bg-gray-100 px-1.5 py-0.5">{{ u.role }}</span>
                                @if (u.community?.name) {
                                    <span class="ml-1">· {{ u.community?.name }}</span>
                                }
                            </div>
                        </div>
                        <span [matTooltip]="u.role === 'super_admin' ? ('IMPERSONATION.DISABLED_SUPER_ADMIN' | transloco) : ''" matTooltipPosition="left">
                            <button
                                mat-stroked-button
                                color="primary"
                                [attr.aria-label]="('IMPERSONATION.IMPERSONATE' | transloco) + ' ' + u.firstname + ' ' + u.lastname"
                                [disabled]="u.role === 'super_admin' || starting"
                                (click)="impersonate(u)">
                                {{ 'IMPERSONATION.IMPERSONATE' | transloco }}
                            </button>
                        </span>
                    </div>
                }
            </div>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close [disabled]="starting">{{ 'IMPERSONATION.CANCEL' | transloco }}</button>
        </mat-dialog-actions>
    `,
})
export class ImpersonationSearchDialogComponent implements OnInit {
    private _impersonation = inject(ImpersonationService);
    private _cdr = inject(ChangeDetectorRef);
    private _dialogRef = inject(MatDialogRef<ImpersonationSearchDialogComponent>);
    private _destroyRef = inject(DestroyRef);
    private _t = inject(TranslocoService);

    search = new FormControl<string>('', { nonNullable: true });
    results: ImpersonationSearchUser[] = [];
    loading = false;
    searched = false;
    starting = false;
    errorMsg = '';

    ngOnInit(): void {
        this.search.valueChanges
            .pipe(
                debounceTime(350),
                distinctUntilChanged(),
                tap(() => {
                    this.errorMsg = '';
                }),
                switchMap((term): Observable<ImpersonationSearchUser[] | null> => {
                    const q = (term ?? '').trim();
                    if (q.length < 2) {
                        // Query demasiado corta: resetear sin marcar `searched` (evita un
                        // "No results" falso al borrar el input).
                        this.results = [];
                        this.searched = false;
                        this.loading = false;
                        this._cdr.markForCheck();
                        return of(null);
                    }
                    // Limpiar resultados previos para que no queden filas stale clickeables
                    // mientras corre la nueva búsqueda.
                    this.results = [];
                    this.loading = true;
                    this._cdr.markForCheck();
                    return this._impersonation.searchUsers(q).pipe(
                        catchError(() => {
                            this.errorMsg = this._t.translate('IMPERSONATION.SEARCH_ERROR');
                            return of([] as ImpersonationSearchUser[]);
                        })
                    );
                }),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe((users) => {
                if (users === null) return; // query corta: estado ya reseteado
                this.results = users;
                this.searched = true;
                this.loading = false;
                this._cdr.markForCheck();
            });
    }

    /** Iniciales para el avatar placeholder. */
    initials(u: ImpersonationSearchUser): string {
        return `${u.firstname?.[0] ?? ''}${u.lastname?.[0] ?? ''}`;
    }

    /**
     * @description Inicia la impersonation del usuario seleccionado. En éxito la app se
     * recarga (el diálogo desaparece); en error muestra el mensaje inline (traducido).
     * @param {ImpersonationSearchUser} u
     */
    async impersonate(u: ImpersonationSearchUser): Promise<void> {
        if (this.starting) return;
        this.starting = true;
        this.errorMsg = '';
        this.search.disable({ emitEvent: false });
        this._cdr.markForCheck();
        try {
            await this._impersonation.startImpersonation(u.uuid);
            // En éxito, startImpersonation hace location.reload(); cerramos por si acaso.
            this._dialogRef.close(true);
        } catch (err: any) {
            const code = err?.message ?? '';
            // El service lanza claves i18n (IMPERSONATION.ERR_*). Cualquier otro error (p.ej.
            // fallo HTTP de transporte) cae al mensaje genérico traducido, nunca al raw.
            this.errorMsg = code.startsWith('IMPERSONATION.') ? this._t.translate(code) : this._t.translate('IMPERSONATION.START_ERROR');
            this.starting = false;
            this.search.enable({ emitEvent: false });
            this._cdr.markForCheck();
        }
    }
}
